import { DirectionalHint, Stack, TooltipHost } from '@fluentui/react';
import clsx from 'clsx';
import {
  FormHookDropdown,
  FormHookTextField,
} from 'common/components/FormHooksFields';
import { FormHookCheckBox } from 'common/components/FormHooksFields/FormHookCheckBox';
import { ColorPickerCallout } from 'documents/personalPool/Signature/ColorPickerCallout';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ApprovalSetupFlowCommonData } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/ApprovalSetupFlowCommonData';
import { FlowApproval } from 'settings/account/intuitiveApprovalSetupFlow/__generated__/FlowApproval';
import { ApprovalValues } from '../../../types';
import { ApprovalHistoryView } from './approvalHistory';
import { ColorHighLights } from './constants';
import { useStyles } from './index.styles';
import { Signatures } from './signatures';
import { setUserDefaults } from 'utility/cache/ui';
import { useReactiveVar } from '@apollo/client';
interface BasicFormProps {
  isNew: boolean;
  approvalDetails: FlowApproval | undefined;
  data: ApprovalSetupFlowCommonData | undefined;
}

export const BasicForm: React.FC<BasicFormProps> = ({
  isNew,
  approvalDetails,
  data,
}) => {
  const style = useStyles();
  const userDefaults = useReactiveVar(setUserDefaults);
  const { setValue, watch } = useFormContext<ApprovalValues>();
  const watchBadgeColor = watch('badgeColor');
  const approvalTypeOptions =
    data?.approvalTypes?.nodes.map((item) => ({
      key: item.id,
      text: item.approvalType || '',
    })) || [];
  const projectPhaseOptions =
    data?.approvalPhases?.nodes.map((item) => ({
      key: item.id,
      text: item.phaseName || '',
    })) || [];

  const isDisabled = isNew ? false : !approvalDetails?.approval?._isUpdatable;

  const PROJECT_PHASE_TOOLTIP =
    'When selected, this approval flow will only be used when this phase matches the company’s approval phase.';
  const ALLOW_ADDING_APPROVERS_TOOLTIP =
    'If checked, pending approvers with the "Add Approver" privilege are capable of adding additional approvers to their approval level.';
  const ALLOW_DELAYED_APPROVERS_TOOLTIP =
    'If checked, the Delayed Approver feature is enabled for approval level(s) that have delayed approver rules.';
  const AUTHORIZED_TOOLTIP =
    'Only authorized approval flows will be considered when a transaction approval request is initially submitted.';
  return (
    <>
      <Stack
        horizontal
        tokens={{ childrenGap: 50 }}
        className={style.mainContainer}
      >
        <Stack
          grow={1}
          tokens={{ childrenGap: 20 }}
          className={style.detailsContainer}
        >
          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={style.flex50}>
              <FormHookDropdown
                label="Approval Type"
                placeholder="Select the type of transaction for this approval"
                name="approvalTypeId"
                options={approvalTypeOptions || []}
                disabled={isDisabled}
                required
                calloutProps={{
                  styles: {
                    root: { width: '25%' },
                  },
                }}
              />
            </Stack.Item>
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={style.flex50}>
              <FormHookTextField
                disabled={isDisabled}
                name="name"
                label="Approval Flow Name"
                placeholder="Enter a name that best describes this approval"
                required
              />
            </Stack.Item>
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={style.flex50}>
              <FormHookTextField
                disabled={isDisabled}
                name="description"
                label="Description"
                placeholder="Additional details (optional)"
              />
            </Stack.Item>
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack.Item className={style.flex50}>
              <FormHookDropdown
                label="Project Phase"
                placeholder="Select phase (optional)"
                name="approvalPhaseTypeId"
                options={projectPhaseOptions || []}
                disabled={isDisabled}
                toolTipText={PROJECT_PHASE_TOOLTIP}
                directionalHint={DirectionalHint.topLeftEdge}
              />
            </Stack.Item>
          </Stack>

          <Stack tokens={{ childrenGap: 40, padding: '20px 0px' }}>
            <Stack
              horizontal
              horizontalAlign="start"
              verticalAlign="center"
              tokens={{ childrenGap: 50 }}
            >
              <TooltipHost
                content={ALLOW_ADDING_APPROVERS_TOOLTIP}
                id="allowAddingApprovers"
              >
                <FormHookCheckBox
                  label="Allow Adding Approvers?"
                  boxSide="end"
                  name="isAddApproversEligible"
                />
              </TooltipHost>
              <TooltipHost
                content={ALLOW_DELAYED_APPROVERS_TOOLTIP}
                id="allowDelayedApprovers"
              >
                <FormHookCheckBox
                  label="Allow Delayed Approvers?"
                  boxSide="end"
                  name="isDelayedApproverAllowed"
                />
              </TooltipHost>
              {!!userDefaults?._isNewProjectSelectionAvailable && <FormHookCheckBox
                label="Include in New Projects?"
                boxSide="end"
                name="isNewProjectIncluded"
              />}
            </Stack>

            <Stack
              horizontal
              horizontalAlign="start"
              verticalAlign="center"
              tokens={{ childrenGap: 120 }}
            >
              <Stack horizontal tokens={{ childrenGap: 5 }}>
                <ColorPickerCallout
                  title="Highlight"
                  disableCell="RED"
                  columnCount={8}
                  colorCells={ColorHighLights}
                  onColorChange={(color) => {
                    const selectedColor = ColorHighLights.find(
                      (item) => item.color === color
                    );

                    if (selectedColor) {
                      setValue('badgeColor', selectedColor.id, {
                        shouldDirty: true,
                      });
                    }
                  }}
                />

                <Stack
                  style={{
                    width: 100,
                    height: 35,
                    borderRadius: 5,
                    border: '1px solid grey',
                    backgroundColor: ColorHighLights.find(
                      (item) => item.id === watchBadgeColor
                    )?.color,
                  }}
                />
              </Stack>

              <TooltipHost content={AUTHORIZED_TOOLTIP} id="authorizedHint">
                <FormHookCheckBox
                  label="Authorized"
                  boxSide="end"
                  name="isNewApprovalSelectable"
                />
              </TooltipHost>
            </Stack>
          </Stack>

          <ApprovalHistoryView
            approvalDetails={approvalDetails}
            isNew={isNew}
          />
          <Signatures approvalDetails={approvalDetails} isNew={isNew} />
          <Stack>
            <Stack style={{ height: '200px' }} />
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};
